<template>
  <v-dialog
    :model-value="true"
    max-width="1024px"
    scrollable
    persistent
    @update:model-value="close"
  >
    <v-card :loading="formProcessing">
      <v-card-title class="font-weight-bold">
        {{ formTitle }}
      </v-card-title>

      <v-card-text>
        <v-container>
          <v-form>
            <v-row>
              <!-- Campaign selection for testing -->
              <v-col cols="6">
                <v-select
                  v-model="formData.campaign_id"
                  :items="campaignOptions"
                  item-title="text"
                  item-value="value"
                  required
                  label="Campaign"
                  :error-messages="validation(v$.formData.campaign_id, 'Campaign')"
                  @input="v$.formData.campaign_id.$touch()"
                  @blur="v$.formData.campaign_id.$touch()"
                />
              </v-col>

              <!-- Enable/Disable campaign -->
              <v-col cols="6">
                <v-select
                  v-model="formData.active"
                  :items="activeOptions"
                  item-title="text"
                  item-value="value"
                  required
                  label="Enabled"
                  :error-messages="validation(v$.formData.active, 'Enabled')"
                  @input="v$.formData.active.$touch()"
                  @blur="v$.formData.active.$touch()"
                />
              </v-col>
            </v-row>

            <!-- Redirect campaigns for SSNs ending in 0-9 -->
            <v-row>
              <v-col
                v-for="i in 10"
                :key="i"
                cols="6"
              >
                <v-select
                  v-model="formData[`ssn_ends_in_${i - 1}`]"
                  :items="redirectCampaignOptions"
                  item-title="text"
                  item-value="value"
                  required
                  :label="`SSN Ends in ${i - 1}`"
                  :error-messages="validation(v$.formData[`ssn_ends_in_${i - 1}`], `SSN Ends in ${i - 1}`)"
                  @input="v$.formData[`ssn_ends_in_${i - 1}`].$touch()"
                  @blur="v$.formData[`ssn_ends_in_${i - 1}`].$touch()"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer />

        <v-btn
          color="blue darken-1"
          text
          :disabled="formProcessing"
          @click="close()"
        >
          Cancel
        </v-btn>

        <v-btn
          color="blue darken-1"
          text
          :disabled="formProcessing"
          @click="submitForm()"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { mapActions } from 'vuex';

export default {
  props: {
    initialData: {
      type: Object,
      default: () => {},
    },

    campaigns: {
      type: Array,
      default: () => [],
    },
  },
  setup() { return { v$: useVuelidate() }; },

  data() {
    return {
      formProcessing: false,

      activeOptions: [
        { value: true, text: 'true' },
        { value: false, text: 'false' },
      ],

      formData: {
        campaign_id: '',
        ssn_ends_in_0: null,
        ssn_ends_in_1: null,
        ssn_ends_in_2: null,
        ssn_ends_in_3: null,
        ssn_ends_in_4: null,
        ssn_ends_in_5: null,
        ssn_ends_in_6: null,
        ssn_ends_in_7: null,
        ssn_ends_in_8: null,
        ssn_ends_in_9: null,
        active: false,
      },
    };
  },

  validations: {
    formData: {
      campaign_id: { required },
      ssn_ends_in_0: { required },
      ssn_ends_in_1: { required },
      ssn_ends_in_2: { required },
      ssn_ends_in_3: { required },
      ssn_ends_in_4: { required },
      ssn_ends_in_5: { required },
      ssn_ends_in_6: { required },
      ssn_ends_in_7: { required },
      ssn_ends_in_8: { required },
      ssn_ends_in_9: { required },
      active: { required },
    },
  },

  computed: {
    formTitle() {
      return this.isNewItem ? 'Add Campaign Testing' : 'Edit Campaign Testing';
    },

    isNewItem() {
      return !this.initialData.id;
    },

    campaignOptions() {
      return this.campaigns.map((item) => ({
        value: item.id,
        text: item.campaign_name,
      }));
    },

    redirectCampaignOptions() {
      return this.campaigns
        .map((item) => ({
          value: item.id,
          text: item.campaign_name,
        }));
    },
  },

  created() {
    this.formData = Object.assign(this.formData, this.initialData);
  },

  methods: {
    ...mapActions('notification', ['setNotification']),

    submitForm() {
      this.v$.$touch();

      if (this.v$.$invalid) {
        return;
      }

      this.formProcessing = true;

      const payload = { ...this.formData };

      // Determine if we are creating a new campaign or updating an existing one
      const request = this.isNewItem
        ? this.$axios.post('api/v1/test-campaigns', payload)
        : this.$axios.put(`api/v1/test-campaigns/${this.initialData.id}`, payload);

      request
        .then(() => {
          this.setNotification({
            message: 'Campaign Testing saved successfully',
            type: 'success',
          });

          this.$emit('item-saved');
          this.close();
        })
        .catch((error) => {
          if (error.response.data.message) {
            this.setNotification({
              message: error.response.data.message,
              color: 'red',
            });
          } else {
            this.setNotification({
              message: 'There was an error processing your request.',
              color: 'red',
            });
          }
        })
        .finally(() => {
          this.formProcessing = false;
        });
    },

    close() {
      this.v$.$reset();
      this.$emit('close-modal');
    },

    validation(field, name) {
      const errors = [];

      if (!field.$dirty) {
        return errors;
      }
      if (field.$error) {
        errors.push(`${name} is Required`);
      }
      if (field.$anyError) {
        errors.push(`${name} is Not Valid`);
      }
      return errors;
    },
  },
};
</script>

<style></style>
